'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { sendGTMEvent } from '@next/third-parties/google'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { KeyboardEvent, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import { usePostBecomeAPartner } from '@/api/partners/become-a-partner-mutation'
import { Button } from '@/components/button'
import { COUNTRY_OPTIONS, CountryCodeSelect } from '@/components/country-code-select'
import { Input } from '@/components/input/input'
import { Form, FormProvider } from '@/components/RHF/form'
import { FormField } from '@/components/RHF/form-field'
import { GooglePlacesBecomeAPartnerSearch } from '@/features/google-places/google-places-become-a-partner-search'
import { IGoogleAddressOption } from '@/features/google-places/google-places-restaurants-search'
import { parseRegionFromLocale } from '@/setup/locales'
import { noOp } from '@/utils/functions/noOp'
import { formatToE164 } from '@/utils/phoneNumber/E164'

const getPartnerFormSchema = (t: (key: string) => string) =>
    z.object({
        name: z.string().min(1, t('form.name.error')),
        countryCode: z.object(
            { label: z.string(), value: z.string() },
            { message: t('form.countryCode.error') }
        ),
        phoneNumber: z.string().min(1, t('form.phoneNumber.error')),
        email: z.string().email(t('form.email.error')),
        restaurant: z.object(
            {
                name: z.string().min(1, t('form.restaurant.error')),
                address: z.string().min(1, t('form.restaurant.error')),
                city: z.string().min(1, t('form.restaurant.error')),
                country: z.string().min(1, t('form.restaurant.error')),
                zip: z.string().min(1, t('form.restaurant.error')),
            },
            { message: t('form.restaurant.error') }
        ),
    })

type PartnerForm = z.infer<ReturnType<typeof getPartnerFormSchema>>

export const BecomeAPartnerHero = () => {
    const searchParams = useSearchParams()

    const locale = useLocale()

    const region = parseRegionFromLocale(locale)

    const t = useTranslations('becomeAPartner')

    const form = useForm<PartnerForm>({
        mode: 'onBlur',
        resolver: zodResolver(getPartnerFormSchema(t)),
        defaultValues: {
            name: '',
            countryCode: COUNTRY_OPTIONS.find((option) => option.value === region),
            phoneNumber: '',
            email: '',
        },
    })

    const { mutateAsync: postBecomeAPartnerAsync } = usePostBecomeAPartner()

    const isFormStateValid = form.formState.isValid

    const handleSubmit: SubmitHandler<PartnerForm> = useCallback(
        async (values) => {
            const ads = searchParams.get('utm')
            const fbclid = searchParams.get('fbclid')

            const nameParts = values.name.split(' ')

            const firstName = nameParts?.[0] ?? ''
            const lastName = nameParts?.slice(1).join(' ') || ''

            const phone = formatToE164({
                countryCallingCode: values.countryCode.label,
                nationalNumber: values.phoneNumber,
            })

            const payload = {
                origin: fbclid ? 'meta' : undefined,
                contact: {
                    firstname: firstName,
                    lastname: lastName,
                    email: values.email,
                    phone,
                    ads: ads ?? undefined,
                },

                company: {
                    name: values.restaurant.name,
                    city: values.restaurant.city,
                    country: values.restaurant.country,
                    zip: values.restaurant.zip,
                    address: values.restaurant.address,
                },
            }

            await postBecomeAPartnerAsync({ body: payload })

            sendGTMEvent({
                event: 'partner_form_submit',
                enhanced_conversion_data: { email: values.email, phone_number: phone },
            })
        },
        [searchParams, postBecomeAPartnerAsync]
    )

    const handleKeyDownSubmit = useCallback(
        async (e: KeyboardEvent) => {
            if (e.key === 'Enter' && isFormStateValid) {
                await handleSubmit(form.getValues())
            }
        },
        [form, isFormStateValid, handleSubmit]
    )

    return (
        <div className="flex flex-col gap-16 pb-20 items-center xl:flex-row xl:items-start max-w-[1000px] mr-auto ml-auto">
            <div className="flex w-full flex-col items-center xl:items-start text-center xl:text-left xl:w-[50%]">
                <Image
                    src="/_images/become-a-partner/hero.jpg"
                    alt="Hero image"
                    width={4000}
                    height={2132}
                    sizes="100vw"
                    className="w-full h-auto object-contain rounded-[20px] mb-6 max-w-[690px]"
                />

                <h1 className="text-black font-bold text-2xl leading-8 mb-4 sm:text-[32px] sm:leading-9 md:max-w-[400px] md:text-[48px] md:leading-[52px]">
                    {t('hero.title')}
                </h1>

                <p className="text-black/50 font-medium leading-6 md:max-w-[75%]">
                    {t('hero.description')}
                </p>
            </div>

            <div className="flex flex-col items-center w-full xl:w-[50%] xl:mt-10">
                <div className="flex flex-col items-center w-full md:max-w-[358px]">
                    {form.formState.isSubmitted ? (
                        <>
                            <div className="flex items-center justify-center p-2 w-12 h-12 rounded-m bg-[#BAFAD4] mb-6">
                                <svg
                                    width="15"
                                    height="13"
                                    viewBox="0 0 15 13"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M14.8284 2.82843L5.41421 12.2426L0 6.82843L2.82843 4L5.41421 6.58579L12 0L14.8284 2.82843Z"
                                        fill="#11301D"
                                    />
                                </svg>
                            </div>

                            <h3 className="text-center font-bold text-3xl mb-3">
                                {t('form.submit.success.title')}
                            </h3>

                            <p className="text-center text-black/70">
                                {t('form.submit.success.description')}
                            </p>
                        </>
                    ) : (
                        <div className="w-full">
                            <h2 className="text-black text-center font-bold text-2xl leading-8 mb-6">
                                {t('form.title')}
                            </h2>

                            <FormProvider {...form}>
                                <Form
                                    className="flex flex-col gap-3 "
                                    onSubmit={form.handleSubmit(handleSubmit)}
                                    onKeyDown={handleKeyDownSubmit}
                                >
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        styleType="small"
                                        render={(fieldProps) => (
                                            <Input
                                                inputClassName="p-4"
                                                placeholder={t('form.name.placeholder')}
                                                {...fieldProps.field}
                                                onValueChange={fieldProps.field.onChange}
                                            />
                                        )}
                                    />

                                    <div className="relative z-dropdown grid grid-cols-[140px_1fr] gap-2">
                                        <FormField
                                            control={form.control}
                                            name="countryCode"
                                            styleType="small"
                                            render={(fieldProps) => {
                                                return (
                                                    <CountryCodeSelect
                                                        onChange={fieldProps.field.onChange}
                                                        value={fieldProps.field.value}
                                                        options={COUNTRY_OPTIONS}
                                                        error={fieldProps.fieldState.error?.message}
                                                    />
                                                )
                                            }}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="phoneNumber"
                                            styleType="small"
                                            render={(fieldProps) => {
                                                const onChange = (value: string) => {
                                                    const cleanedPhoneNumber = value.replace(
                                                        /\D/g,
                                                        ''
                                                    )

                                                    fieldProps.field.onChange(cleanedPhoneNumber)
                                                }
                                                return (
                                                    <Input
                                                        placeholder={t(
                                                            'form.phoneNumber.placeholder'
                                                        )}
                                                        {...fieldProps.field}
                                                        onValueChange={onChange}
                                                    />
                                                )
                                            }}
                                        />
                                    </div>

                                    <FormField
                                        control={form.control}
                                        name="email"
                                        styleType="small"
                                        render={(fieldProps) => (
                                            <Input
                                                placeholder={t('form.email.placeholder')}
                                                {...fieldProps.field}
                                                onValueChange={fieldProps.field.onChange}
                                            />
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="restaurant"
                                        styleType="small"
                                        render={(fieldProps) => {
                                            const onSelect = (
                                                option: IGoogleAddressOption | null
                                            ) => {
                                                fieldProps.field.onChange(
                                                    option
                                                        ? {
                                                              name: option?.value.name,
                                                              address: option?.label,
                                                              city: option?.value.city,
                                                              country: option?.value.country,
                                                              zip: option?.value.zip,
                                                          }
                                                        : undefined
                                                )
                                            }

                                            return (
                                                <GooglePlacesBecomeAPartnerSearch
                                                    onSelect={onSelect}
                                                    onBlur={fieldProps.field.onBlur}
                                                    placeholder={t('form.restaurant.placeholder')}
                                                />
                                            )
                                        }}
                                    />

                                    <Button
                                        onClick={noOp}
                                        styleType="unstyled"
                                        className="w-full bg-neotaste text-black rounded-l h-[40px] p-3"
                                        type="submit"
                                        disabled={!isFormStateValid}
                                        isLoading={form.formState.isSubmitting}
                                    >
                                        {t('form.submit.title')}
                                    </Button>
                                </Form>
                            </FormProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
